// TODO WILL DO THIS PAGE THE BUTTON ISNT ALL THE WAY DONE. IT MAY LOOK IT.
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { useUsersList } from '@/contexts/UsersListProvider';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useTheme } from '@/contexts/ThemeContext';

// Import SupportingDocs namespace for upload
// @ts-ignore
// eslint-disable-next-line
import { SupportingDocs } from 'js/external-js/variableModules/namespaces/SupportingDocs.namespace';
import { getAsset, updateAsset } from '@/services/assetsService';
import { Timestamp } from 'firebase/firestore';
import { createDocument } from '@/services/documentsService';
import { useCurrentUser } from '@/contexts/TenancyContext';
import HelpPopoverHover, { HelpPopoverHoverProps } from '@/components/HelpPopoverHover';
import { cleanDateAndTimeObject } from '@shared/renderDate';

// Define types for vendor security utilities
type VendorSecurityResult = {
  score: number | null;
  error: string | null;
  cached?: boolean; // Made optional to match the actual implementation
};

// Will be loaded dynamically when needed
let checkVendorSecurityUtil: (assetId: string) => Promise<VendorSecurityResult>;
let getSecurityScoreLabel: (score: number | null) => string;
let getSecurityScoreColor: (score: number | null) => string;
let formatSecurityScore: (score: number | null) => string;

interface ReviewAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvent?: {
    title?: string;
    description?: string;
    date?: string;
    id?: string;
    prevReviewDate?: string;
    reviewDate?: string;
    nextReviewDate?: string;
    lastReviewDate?: string;
    rawData?: {
      vendorWebsite?: string;
      componentId?: string;
      componentID?: string;
      [key: string]: any;
    };
  };
  onDataChanged?: () => void;
  helpData?: HelpPopoverHoverProps;
}

interface AssetInstance {
  id?: string;
  vendorWebsite?: string;
  [key: string]: any;
}

function ReviewedByCombobox({ reviewedBy, setReviewedBy }: { reviewedBy: string, setReviewedBy: (v: string) => void }) {
  const { users, loading } = useUsersList();
  React.useEffect(() => {
    if (!reviewedBy && users.length > 0 && !loading) {
      setReviewedBy(users[0].value);
    }
  }, [users, reviewedBy, setReviewedBy, loading]);
  return (
    <SingleUserCombobox
      selected={reviewedBy}
      setSelected={setReviewedBy}
      placeholder="Select reviewer..."
      className="w-full"
    />
  );
}

const ReviewAssetDialog: React.FC<ReviewAssetDialogProps> = ({ open, onOpenChange, selectedEvent, onDataChanged, helpData }) => {
  const [securityScore, setSecurityScore] = useState<number | null>(null);
  const [securityCheckError, setSecurityCheckError] = useState<string | null>(null);
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(false);
  const [isScoreCached, setIsScoreCached] = useState<boolean>(false);


  // Reset all dialog state when dialog opens or selectedEvent changes
  React.useEffect(() => {
    if (!open) return;
    setReviewedBy("");
    setTreatmentPlan('');
    setReviewCompleted('no');
    setSupportingDocs([]);
    setUploading(false);
    setUploadError(null);
    setComments('');
    setSecurityScore(null);
    setSecurityCheckError(null);
    setIsCheckingSecurity(false);
    setLastReviewDate(selectedEvent?.lastReviewDate ? new Date(selectedEvent.lastReviewDate) : undefined);
    setNextReviewDate(selectedEvent?.nextReviewDate ? new Date(selectedEvent.nextReviewDate) : undefined);
    setLoading(false);
    setError(null);
  }, [open, selectedEvent]);
  const { isDark } = useTheme();
  const [reviewedBy, setReviewedBy] = React.useState("");
  const [treatmentPlan, setTreatmentPlan] = React.useState('');
  const [reviewCompleted, setReviewCompleted] = React.useState('no');
  const [supportingDocs, setSupportingDocs] = React.useState<File[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [comments, setComments] = React.useState('');
  const [lastReviewDate, setLastReviewDate] = React.useState<Date | undefined>(new Date());
  const [nextReviewDate, setNextReviewDate] = React.useState<Date | undefined>(new Date(new Date().setMonth(new Date().getMonth() + 12)));
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragActive = React.useRef(false);
  const currentUser = useCurrentUser();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Check vendor security when the dialog opens or selectedEvent changes
  useEffect(() => {
    if (!open || !selectedEvent?.id) {
      // Reset state when dialog is closed or no event selected
      setSecurityScore(null);
      setSecurityCheckError(null);
      setIsCheckingSecurity(false);
      return;
    }

    const checkSecurity = async () => {
      if (!selectedEvent?.id) {
        setSecurityCheckError('No asset selected');
        return;
      }

      // DED-1499: Check if vendor monitoring is enabled before fetching score
      const rawData = selectedEvent?.rawData;
      const vendorMonitoringRaw = rawData?.vendorMonitoringEnabled ?? rawData?.vendorManagementNeeded;
      let monitoringEnabled = false;
      if (typeof vendorMonitoringRaw === 'string') {
        const normalized = vendorMonitoringRaw.trim().toLowerCase();
        monitoringEnabled = normalized === 'yes' || normalized === 'true' || normalized === 'enabled';
      } else if (typeof vendorMonitoringRaw === 'boolean') {
        monitoringEnabled = vendorMonitoringRaw;
      }

      if (!monitoringEnabled) {
        // Vendor monitoring is disabled, don't try to fetch score
        setSecurityScore(null);
        setSecurityCheckError(null);
        setIsCheckingSecurity(false);
        return;
      }

      try {
        setIsCheckingSecurity(true);
        setSecurityCheckError(null);

        console.log('[ReviewAssetDialog] Checking vendor security for asset ID:', selectedEvent.id);

        // Dynamically import the vendor security utilities only when needed
        if (!checkVendorSecurityUtil || !getSecurityScoreColor) {
          console.log('[ReviewAssetDialog] Lazy loading vendor security utilities');
          try {
            const vendorSecurity = await import('@/utils/vendorSecurity');
            checkVendorSecurityUtil = vendorSecurity.checkVendorSecurity;
            getSecurityScoreLabel = vendorSecurity.getSecurityScoreLabel;
            getSecurityScoreColor = vendorSecurity.getSecurityScoreColor;
            formatSecurityScore = vendorSecurity.formatSecurityScore;
          } catch (importError) {
            console.error('[ReviewAssetDialog] Failed to load vendor security utilities:', importError);
            setSecurityCheckError('Failed to load security tools');
            return;
          }
        }

        // Use the checkVendorSecurityUtil function with the asset ID
        const { score, error, cached = false } = await checkVendorSecurityUtil(selectedEvent.id);

        setIsScoreCached(cached);

        if (error) {
          // Only show error if it's not just a missing vendor website
          if (error !== 'No vendor website available') {
            setSecurityCheckError(error);
          }
          setSecurityScore(null);
        } else {
          setSecurityScore(score);
        }
      } catch (error) {
        console.error('[ReviewAssetDialog] Error checking vendor security:', error);
        setSecurityCheckError('Failed to check vendor security');
        setSecurityScore(null);
        setIsScoreCached(false);
      } finally {
        setIsCheckingSecurity(false);
      }
    };

    checkSecurity();
    console.log('[ReviewAssetDialog] Selected event:', selectedEvent);
    // Set default values for fields
    // if (!selectedEvent?.rawData?.lastReviewDate) {
    //   setLastReviewDate(new Date());
    // } else {
    //   setLastReviewDate(cleanDateAndTimeObject(selectedEvent.rawData.lastReviewDate));
    // }
    setLastReviewDate(new Date());

    // if (!selectedEvent?.rawData?.nextReviewDate) {
    setNextReviewDate(new Date(new Date().setMonth(new Date().getMonth() + 12)));
    // } else {
    //   setNextReviewDate(cleanDateAndTimeObject(selectedEvent.rawData.nextReviewDate));
    // }

  }, [open, selectedEvent?.id]);

  // Utility to remove undefined fields
  function removeUndefined(obj: any) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
  }

  // --- Modernized Complete Review handler ---
  const handleCompleteReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (!selectedEvent?.id) throw new Error('No asset ID provided');
      const assetID = selectedEvent.id;
      // 1. Fetch the asset instance (optional, but good for validation)
      const assetInstance = await getAsset(assetID);
      if (!assetInstance) throw new Error('Asset not found');

      // 2. Prepare asset history entry for review
      const prevAsset = assetInstance.entity || {};
      const now = new Date();
      const reviewFields = {
        lastReviewDate: lastReviewDate ? lastReviewDate : now,
        nextReviewDate: nextReviewDate ? nextReviewDate : new Date(new Date().setMonth(now.getMonth() + 12)),
        reviewedBy,
        reviewCompleted,
        comments,
      };
      // Compare fields to find changes
      const changes = Object.entries(reviewFields).reduce((arr, [key, newValue]) => {
        const oldValue = prevAsset[key];
        // Compare value (handle date objects)
        const oldValStr = oldValue instanceof Timestamp ? oldValue.toMillis() : String(oldValue ?? '');
        const newValStr = newValue instanceof Timestamp ? newValue.toMillis() : String(newValue ?? '');
        if (oldValStr !== newValStr) {
          arr.push({ elementName: key, oldValue: oldValue ?? null, newValue });
        }
        return arr;
      }, [] as Array<{ elementName: string; oldValue: any; newValue: any }>);
      // Build new history entry
      const newHistoryEntry = {
        historyType: 'review',
        changedBy: currentUser.uid,
        changedDate: Timestamp.fromDate(now),
        changes,
      };
      // Append to assetHistory (ensure it's an array)
      const assetHistory: any[] = Array.isArray((prevAsset as any).assetHistory)
        ? [...(prevAsset as any).assetHistory, newHistoryEntry]
        : [newHistoryEntry];
      // Optionally update summary fields
      const reviewData: any = {
        assetHistory,
        lastReviewDate: reviewFields.lastReviewDate,
        nextReviewDate: reviewFields.nextReviewDate,
      };
      await updateAsset(assetID, reviewData);

      // 3. Upload supporting docs as new documents
      if (supportingDocs.length > 0) {
        for (const file of supportingDocs) {
          const docData = {
            classification: 'Customer Confidential',
            comments: comments || '',
            confluenceURL: '',
            dateUploaded: Timestamp.fromDate(new Date()),
            docHash: '', // will be set by backend
            documentHistory: {},
            documentType: 'Evidence',
            fileName: file.name,
            lastReviewDate: lastReviewDate ? Timestamp.fromDate(lastReviewDate) : undefined,
            msgPages: [],
            nextReviewDate: nextReviewDate ? Timestamp.fromDate(nextReviewDate) : undefined,
            procRef: [],
            relatedControls: [],
            updateMethod: '',
            updatedBy: currentUser.uid,
            uploadedBy: currentUser.uid,
            companyID: window.companyID,
          };
          const docInstance = new SupportingDocs.Document(docData);
          await createDocument(docInstance, file);
        }
        setSupportingDocs([]);
      }
      if (typeof onDataChanged === 'function') onDataChanged();
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Error completing asset review');
    } finally {
      setLoading(false);
    }
  };

  // Utility to generate a GUID for docID
  function generateGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // Enhanced: handle multiple files (append, not replace)
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSupportingDocs(prev => {
        const newFiles = Array.from(e.target.files);
        // Prevent duplicates (by name + size)
        const fileMap = new Map(prev.map(f => [f.name + f.size, f]));
        for (const file of newFiles) {
          fileMap.set(file.name + file.size, file);
        }
        return Array.from(fileMap.values());
      });
    }
  }, []);

  // Replace drag area with styled button
  const handleAttachDocument = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // --- Card grid for file previews ---
  const getFileIcon = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (file.type.startsWith('image/')) {
      return <img src={URL.createObjectURL(file)} alt={file.name} className="w-12 h-12 object-cover rounded border" />;
    }
    if (ext === 'pdf') return <span className="flex items-center justify-center w-12 h-12 bg-red-100 text-red-600 rounded text-xs font-semibold">PDF</span>;
    if (["doc", "docx"].includes(ext || '')) return <span className="flex items-center justify-center w-12 h-12 bg-blue-100 text-blue-600 rounded text-xs font-semibold">DOC</span>;
    if (["xls", "xlsx", "csv"].includes(ext || '')) return <span className="flex items-center justify-center w-12 h-12 bg-green-100 text-green-600 rounded text-xs font-semibold">XLS</span>;
    return <span className="flex items-center justify-center w-12 h-12 bg-gray-100 text-gray-500 rounded text-xs font-semibold">TXT</span>;
  }, []);

  // Drag and drop on the Attach button
  const handleButtonDragOver = useCallback((e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleButtonDrop = useCallback((e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSupportingDocs(prev => {
        const newFiles = Array.from(e.dataTransfer.files);
        const fileMap = new Map(prev.map(f => [f.name + f.size, f]));
        for (const file of newFiles) {
          fileMap.set(file.name + file.size, file);
        }
        return Array.from(fileMap.values());
      });
    }
  }, []);

  // Add remove handler for attachments
  const handleRemoveAttachment = React.useCallback((fileToRemove: File) => {
    setSupportingDocs(prev => prev.filter(f => !(f.name === fileToRemove.name && f.size === fileToRemove.size)));
  }, []);

  const renderSecurityScore = () => {
    if (isCheckingSecurity) {
      return <div className="text-sm text-gray-500">Checking security score...</div>;
    }

    if (securityCheckError) {
      return <div className="text-sm text-red-500">Error: {securityCheckError}</div>;
    }

    if (securityScore === null) {
      return <div className="text-sm text-gray-500">No security score available</div>;
    }

    const scoreColor = getSecurityScoreColor(securityScore);
    const scoreLabel = getSecurityScoreLabel(securityScore);
    const formattedScore = formatSecurityScore(securityScore);

    return (
      <div className="space-y-1">
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${scoreColor}`}>
            {formattedScore}
          </span>
          <span className="text-sm">{scoreLabel}</span>
        </div>
        {isScoreCached && (
          <div className="text-xs text-gray-500 flex items-center">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Cached result (refreshes weekly)
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card overflow-hidden">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">Asset Review</DialogTitle>
            {/* <DialogDescription className="text-muted-foreground">
            {selectedEvent?.title || 'Complete the asset review and record findings'}
          </DialogDescription> */}
          </div>
          <form onSubmit={handleCompleteReview} className='px-4'>
            <div className="grid gap-4 py-4">
              {/* Name (expandable textarea) */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="asset-name" className="col-span-1 text-sm font-medium text-foreground whitespace-normal break-words">Name</Label>
                <Textarea id="asset-name" value={selectedEvent?.title.replace('Review Asset: ', '') || ''} readOnly className="col-span-3 border border-gray-200 rounded-md resize-y min-h-[40px] max-h-[200px]" />
              </div>
              {/* Reviewed By */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="reviewed-by" className="col-span-1 text-sm font-medium text-foreground whitespace-normal break-words">Reviewed By</Label>
                <div className="col-span-3 w-full">
                  <ReviewedByCombobox reviewedBy={reviewedBy} setReviewedBy={setReviewedBy} />
                </div>
              </div>
              {/* Vendor Score (readonly) */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="vendor-score" className="col-span-1 text-sm font-medium text-foreground whitespace-normal break-words">
                  Vendor Score
                  <HelpPopoverHover config={helpData?.['modal.assetreview.vendorscore']} />
                </Label>
                <div className="col-span-3 flex items-center">
                  {renderSecurityScore()}
                </div>
              </div>
              {/* Review Completed (Select) */}
              {/* <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="review-completed" className="col-span-1 text-sm font-medium text-gray-600 whitespace-normal break-words">Review Completed</Label>
              <div className="col-span-3">
                <Select value={reviewCompleted} onValueChange={setReviewCompleted}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div> */}
              {/* Treatment Plan (conditional, textarea under next review) */}
              {false && (
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="treatment-plan" className="col-span-1 text-sm font-medium text-foreground whitespace-normal break-words">Treatment Plan</Label>
                  <div className="col-span-3">
                    <Textarea id="treatment-plan" value={treatmentPlan} onChange={e => setTreatmentPlan(e.target.value)} placeholder="Describe the treatment plan..." className="border border-gray-200 rounded-md min-h-[40px] max-h-[200px] resize-y" />
                  </div>
                </div>
              )}
              {/* Supporting Docs Preview and Attach */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="attach-doc" className="col-span-1 text-sm font-medium text-foreground whitespace-normal break-words mt-2">Supporting Docs</Label>
                <div className="col-span-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAttachDocument}
                    className="border border-gray-200 hover:bg-gray-50 hover:text-foreground mb-3"
                    onDragOver={handleButtonDragOver}
                    onDrop={handleButtonDrop}
                    style={{ position: 'relative', zIndex: 2 }}
                  >
                    Attach Supporting Document
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                      accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    />
                  </Button>
                  {supportingDocs.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {supportingDocs.map((file, idx) => (
                        <div key={file.name + file.size + idx} className="flex flex-col items-center border rounded p-2 bg-gray-50 relative group">
                          {getFileIcon(file)}
                          <span className="text-xs mt-1 max-w-[80px] truncate" title={file.name}>{file.name}</span>
                          <button
                            type="button"
                            className="absolute top-0 right-0 m-1 p-1 rounded-full bg-white text-gray-500 hover:text-red-600 hover:bg-red-100 shadow group-hover:block"
                            style={{ display: 'block' }}
                            title="Remove attachment"
                            onClick={() => handleRemoveAttachment(file)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {uploadError && <div className="text-red-500 text-xs mt-1">{uploadError}</div>}
                </div>
              </div>
              {/* Last Review Date */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="asset-last-review-date" className="col-span-1 text-sm font-medium text-foreground whitespace-normal break-words">Date Reviewed</Label>
                <div className="col-span-3">
                  <DatePicker date={lastReviewDate} setDate={(date) => setLastReviewDate(date)} fullWidth enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date" />
                </div>
              </div>
              {/* Next Review Date */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="asset-next-review-date" className="col-span-1 text-sm font-medium text-foreground whitespace-normal break-words">Next Review Date</Label>
                <div className="col-span-3">
                  <DatePicker date={nextReviewDate} setDate={setNextReviewDate} fullWidth enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date" />
                </div>
              </div>
              {/* Comments */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="comments" className="col-span-1 text-sm font-medium text-foreground whitespace-normal break-words">Comments</Label>
                <div className="col-span-3">
                  <Textarea id="comments" value={comments} onChange={(e) => setComments(e.target.value)} rows={4} placeholder="Add notes about the asset review..." />
                </div>
              </div>
            </div>
            {/* Error and Loading Feedback */}
            {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
            {/* {loading && <div className="text-blue-500 text-sm mb-2">Saving review...</div>} */}
          </form>
          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <div className="text-xs text-primary mt-2 ml-1 cursor-pointer" onClick={() => window.open(`/assetdetail/${selectedEvent?.id}`, '_blank')} title="View Asset Detail">
              Component ID: {selectedEvent?.rawData?.componentId || selectedEvent?.rawData?.componentID || selectedEvent?.rawData?.assetID ? `AST-${selectedEvent?.rawData?.assetID}` : 'N/A'}
            </div>
            <div className="flex flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border border-gray-200 hover:bg-gray-50 hover:text-black dark:hover:text-white" disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Complete Review'}</Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewAssetDialog;
